import { uiText } from '../i18n/uiText';
import SafeDateInput from '../components/common/SafeDateInput';
import SafeTimeInput from '../components/common/SafeTimeInput';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { auditProgramsApi, auditsApi, outgoingEmailsApi, getApiErrorMessage } from '../api';
import { ClipboardList, FileDown, Pencil, Plus, Save, Send, Trash2, X } from 'lucide-react';
import { LoadingState } from '../components/common/UI';
import ActionMenu from '../components/common/ActionMenu';
import { formatStatus } from '../utils/formatters';
import PageIntro from '../components/ui/PageIntro';
import {useAuth} from '../store/AuthContext';
import {useConfirm} from '../store/ConfirmContext';
import useDraftBuffer from '../hooks/useDraftBuffer';
import DraftRecoveryNotice from '../components/common/DraftRecoveryNotice';
import SaveStatus from '../components/common/SaveStatus';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const emptyScheduleRow = {
  date: '',
  start_time: '',
  end_time: '',
  activity: '',
  process: '',
  department: '',
  requirements_block: '',
  auditee_participants: '',
  auditor_user_id: '',
  auditor_name: '',
};

const defaultForm = () => ({
  id: null,
  audit_id: '',
  plan_id: '',
  number: `Ф-АУД-03/${new Date().getFullYear()}`,
  status: 'draft',
  version: '1.0',
  audit_type: '',
  audit_object: '',
  audited_company_id: '',
  customer_company_id: '',
  object_representative: '',
  site_address: '',
  scope_text: '',
  schedule_items: [{ ...emptyScheduleRow }],
  resolved_audit_team: [],
});

function formFromProgram(item) {
  return {
    ...defaultForm(),
    id: item.id,
    audit_id: item.audit_id ? String(item.audit_id) : '',
    plan_id: item.plan_id || '',
    number: item.number || `Ф-АУД-03/${new Date().getFullYear()}`,
    status: item.status || 'draft',
    version: item.version || '1.0',
    audit_type: item.audit_type || '',
    audit_object: item.audit_object || '',
    audited_company_id: item.audited_company?.id || item.audited_company_id || '',
    customer_company_id: item.customer_company?.id || item.customer_company_id || '',
    object_representative: item.object_representative || '',
    site_address: item.site_address || '',
    scope_text: scopeToText(item.scope_items || []),
    schedule_items: normalizeScheduleRows(item.schedule_items || []),
    resolved_audit_team: item.resolved_audit_team || [],
  };
}

function scopeToText(items = []) {
  return items
    .map((item) => (typeof item === 'string' ? item : item?.title || item?.text || item?.requirement || ''))
    .filter(Boolean)
    .join('\n');
}

function textToScope(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((title) => ({ title }));
}

function programScopeSummary(items = []) {
  const values = items
    .map((item) => (typeof item === 'string' ? item : item?.title || item?.text || item?.requirement || ''))
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return values.length ? values.slice(0, 3).join(' · ') : uiText("Область не указана");
}

function programScheduleSummary(items = []) {
  const values = items
    .map((item) => item?.process || item?.activity || item?.department || '')
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return values.length ? values.slice(0, 2).join(' · ') : uiText("План не заполнен");
}

function normalizeScheduleRows(rows = []) {
  const normalized = rows
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const hasProcess = Object.prototype.hasOwnProperty.call(row, 'process');
      const process = hasProcess ? (row.process || '') : (row.activity || '');
      return {
        ...emptyScheduleRow,
        ...row,
        process,
        activity: process,
        auditor_user_id: row.auditor_user_id || '',
      };
    });
  return normalized.length ? normalized : [{ ...emptyScheduleRow }];
}

function shortDate(value) {
  return value ? String(value).slice(0, 10) : '';
}

function plannedStart(value) {
  const raw = value ? new Date(value) : new Date();
  const date = Number.isNaN(raw.getTime()) ? new Date() : raw;
  if (date.getHours() === 0 && date.getMinutes() === 0) date.setHours(10, 0, 0, 0);
  return date;
}

function formatTime(value) {
  return value.toTimeString().slice(0, 5);
}

function buildScheduleFromAudit(audit) {
  if (!audit) return [{ ...emptyScheduleRow }];
  const start = plannedStart(audit?.scheduled_date);
  const sections = (audit?.template?.sections || [])
    .map((section) => (section?.title || section?.name || '').trim())
    .filter(Boolean);
  const titles = sections.length ? sections : ['Проведение аудита, интервью, анализ документов и фиксация результатов'];
  const lead = (audit?.assignments || []).find((row) => row.status !== 'revoked' && row.team_role === 'lead')
    || (audit?.assignments || []).find((row) => row.status !== 'revoked')
    || null;
  const auditorId = lead?.auditor_user_id || audit?.auditor?.id || '';
  const auditorName = lead?.auditor_name || audit?.auditor?.full_name || '';
  return titles.map((title, index) => {
    const rowStart = new Date(start.getTime() + index * 60 * 60 * 1000);
    const rowEnd = new Date(start.getTime() + (index + 1) * 60 * 60 * 1000);
    const startTime = formatTime(rowStart);
    const endTime = formatTime(rowEnd);
    return {
      ...emptyScheduleRow,
      date: rowStart.toISOString().slice(0, 10),
      start_time: startTime,
      end_time: endTime,
      time: `${startTime}-${endTime}`,
      activity: titles.length > 1 ? `Проверка блока: ${title}` : title,
      process: title,
      auditor_user_id: auditorId,
      auditor_name: auditorName,
    };
  });
}

function buildScopeTextFromAudit(audit) {
  if (!audit) return '';
  const lines = [
    audit?.meta?.audit_basis,
    audit?.template?.name ? `Шаблон проверки: ${audit.template.name}` : '',
    'Договорные и регламентные требования',
    'Процессы и записи проверяемой организации',
  ].map((line) => String(line || '').trim()).filter(Boolean);
  return Array.from(new Set(lines)).join('\n');
}

function formFromAudit(audit, auditId = '') {
  const meta = audit?.meta || {};
  return {
    ...defaultForm(),
    audit_id: String(audit?.id || auditId || ''),
    audit_type: meta.audit_type || audit?.template?.audit_type || '',
    audit_object: meta.audited_company?.name || audit?.title || '',
    audited_company_id: meta.audited_company?.id || '',
    customer_company_id: meta.customer_company?.id || '',
    site_address: meta.site_address || meta.audited_company?.site_address || '',
    object_representative: meta.audited_company?.contact_person || '',
    scope_text: buildScopeTextFromAudit(audit),
    schedule_items: buildScheduleFromAudit(audit),
  };
}

function isEmptySchedule(rows = []) {
  return rows.length === 0 || rows.every((row) => (
    !row.date
    && !row.start_time
    && !row.end_time
    && !row.time
    && !row.activity
    && !row.process
    && !row.department
    && !row.requirements_block
    && !row.auditee_participants
  ));
}

export default function AuditProgramsPage() {
  const {user}=useAuth();
  const confirm=useConfirm();
  const buffer=useDraftBuffer(user?.id,'editor','audit-program');
  const busy=useRef(false);
  const editSequence=useRef(0);
  const [editorLoading,setEditorLoading]=useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedAuditId = searchParams.get('audit_id') || '';
  const requestedProgramId = searchParams.get('program_id') || '';
  const prefillDoneRef = useRef(false);
  const [programs, setPrograms] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const form=buffer.values.form || defaultForm();
  const setForm=value=>buffer.edit('form',typeof value==='function'?value(buffer.values.form || defaultForm()):value);
  const hydrateForm=value=>buffer.hydrate({form:value});
  const canReplace=async()=>!busy.current && (!(buffer.count || buffer.recovery) || await confirm(uiText("Несохранённый план будет заменена. Продолжить?"),{title:uiText("Несохранённые изменения"),confirmLabel:uiText("Заменить форму")}));
  useEffect(()=>()=>{editSequence.current+=1;},[]);

  const load = async () => {
    setLoading(true);
    setError('');
    const [programsRes, auditsRes] = await Promise.allSettled([
      auditProgramsApi.list(),
      auditsApi.list({ detailed: true }),
    ]);
    const messages = [];
    if (programsRes.status === 'fulfilled') setPrograms(Array.isArray(programsRes.value.data) ? programsRes.value.data : []);
    else messages.push(uiText("Планы: {v0}", {v0: (getApiErrorMessage(programsRes.reason))}));
    if (auditsRes.status === 'fulfilled') setAudits(Array.isArray(auditsRes.value.data) ? auditsRes.value.data : []);
    else messages.push(uiText("Аудиты: {v0}", {v0: (getApiErrorMessage(auditsRes.reason))}));
    if (messages.length) setError(messages.join(' · '));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const auditsById = useMemo(() => Object.fromEntries(audits.map((item) => [String(item.id), item])), [audits]);
  const currentAudit = form.audit_id ? auditsById[String(form.audit_id)] : null;
  const currentTeam = form.resolved_audit_team?.length
    ? form.resolved_audit_team
    : (currentAudit?.assignments || []);

  const resetForm = async () => {
    if(!await canReplace()) return;
    editSequence.current+=1;buffer.discard();
    const audit = requestedAuditId ? auditsById[String(requestedAuditId)] : null;
    hydrateForm(audit ? formFromAudit(audit, requestedAuditId) : defaultForm());
    setSuccess('');
  };

  const startCreate = async (auditId = requestedAuditId) => {
    if(!await canReplace()) return;
    editSequence.current+=1;buffer.discard();
    const audit = auditId ? auditsById[String(auditId)] : null;
    hydrateForm(audit ? formFromAudit(audit, auditId) : defaultForm());
    setSuccess('');
    if (auditId) setSearchParams({ audit_id: String(auditId) });
    else setSearchParams({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startEdit = async (program, updateUrl = true) => {
    if(busy.current || (updateUrl && !await canReplace())) return;
    const request=++editSequence.current;
    setEditorLoading(true);
    try {
      const res = await auditProgramsApi.get(program.id);
      if(request!==editSequence.current || !buffer.active) return;
      const item = res.data || program;
      if(updateUrl) buffer.discard();
      hydrateForm(formFromProgram(item));
      setSuccess('');
      if (updateUrl) {
        const params = { program_id: String(item.id) };
        if (item.audit_id) params.audit_id = String(item.audit_id);
        setSearchParams(params);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      if(request===editSequence.current) setEditorLoading(false);
    }
  };

  useEffect(() => {
    if (loading || prefillDoneRef.current) return;
    if (requestedProgramId) {
      prefillDoneRef.current = true;
      startEdit({ id: requestedProgramId }, false);
      return;
    }
    if (!requestedAuditId) return;
    prefillDoneRef.current = true;
    const existing = programs.find((item) => String(item.audit_id) === String(requestedAuditId));
    if (existing) {
      startEdit(existing, false);
      return;
    }
    const audit = auditsById[String(requestedAuditId)];
    hydrateForm(formFromAudit(audit, requestedAuditId));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loading, requestedAuditId, requestedProgramId, programs, auditsById]);

  const handleAuditChange = (auditId) => {
    const audit = auditsById[String(auditId)];
    setForm((prev) => ({
      ...prev,
      audit_id: auditId,
      audit_type: prev.audit_type || audit?.meta?.audit_type || audit?.template?.audit_type || '',
      audit_object: prev.audit_object?.trim() ? prev.audit_object : (audit?.meta?.audited_company?.name || audit?.title || ''),
      audited_company_id: prev.audited_company_id || audit?.meta?.audited_company?.id || '',
      customer_company_id: prev.customer_company_id || audit?.meta?.customer_company?.id || '',
      site_address: prev.site_address?.trim() ? prev.site_address : (audit?.meta?.site_address || audit?.meta?.audited_company?.site_address || ''),
      object_representative: prev.object_representative?.trim() ? prev.object_representative : (audit?.meta?.audited_company?.contact_person || ''),
      scope_text: prev.scope_text?.trim() ? prev.scope_text : buildScopeTextFromAudit(audit),
      schedule_items: isEmptySchedule(prev.schedule_items) ? buildScheduleFromAudit(audit) : prev.schedule_items,
    }));
  };

  const updateSchedule = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      schedule_items: prev.schedule_items.map((row, i) => {
        if (i !== idx) return row;
        const next = { ...row, [field]: value };
        if (field === 'process') next.activity = value;
        return next;
      }),
    }));
  };

  const setScheduleAuditor = (idx, value) => {
    const picked = currentTeam.find((row) => String(row.auditor_user_id) === String(value));
    setForm((prev) => ({
      ...prev,
      schedule_items: prev.schedule_items.map((row, i) => (i === idx
        ? { ...row, auditor_user_id: value, auditor_name: picked?.auditor_name || row.auditor_name || '' }
        : row)),
    }));
  };

  const addScheduleRow = () => {
    setForm((prev) => ({ ...prev, schedule_items: [...prev.schedule_items, { ...emptyScheduleRow }] }));
  };

  const removeScheduleRow = (idx) => {
    setForm((prev) => ({
      ...prev,
      schedule_items: prev.schedule_items.length > 1 ? prev.schedule_items.filter((_, i) => i !== idx) : [{ ...emptyScheduleRow }],
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if(busy.current || editorLoading || buffer.recovery) return;
    busy.current=true;
    const snapshot=buffer.snapshot();
    setSaving(true);
    setError('');
    try {
      const payload = {
        number: form.number,
        audit_id: form.audit_id ? Number(form.audit_id) : null,
        plan_id: form.plan_id ? Number(form.plan_id) : null,
        status: form.status,
        version: form.version || '1.0',
        audit_type: form.audit_type || null,
        audit_object: form.audit_object || null,
        audited_company_id: form.audited_company_id ? Number(form.audited_company_id) : null,
        customer_company_id: form.customer_company_id ? Number(form.customer_company_id) : null,
        object_representative: form.object_representative || null,
        site_address: form.site_address || null,
        scope_items: textToScope(form.scope_text),
        schedule_items: form.schedule_items.map((row) => ({
          ...row,
          activity: row.process || '',
          process: row.process || '',
          auditor_user_id: row.auditor_user_id ? Number(row.auditor_user_id) : null,
          time: row.start_time && row.end_time ? `${row.start_time}-${row.end_time}` : row.start_time || row.end_time || row.time || '',
        })),
      };
      const res = form.id
        ? await auditProgramsApi.update(form.id, payload)
        : await auditProgramsApi.create(payload);
      const saved = res.data;
      if (saved?.id) {
        buffer.acknowledge(snapshot,{committed:true,values:{form:formFromProgram(saved)}});
        if(!buffer.count) hydrateForm(formFromProgram(saved));
        const params = { program_id: String(saved.id) };
        if (saved.audit_id) params.audit_id = String(saved.audit_id);
        setSearchParams(params);
      }
      setSuccess(uiText("План сохранён. Его можно продолжить редактировать, скачать в PDF или направить объекту/заказчику."));
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      busy.current=false;
      setSaving(false);
    }
  };

  const queue = async (id, target) => {
    try {
      await outgoingEmailsApi.queueProgram(id, target);
      alert(target === 'object'
        ? uiText("План поставлен в очередь на отправку объекту и передан на согласование.")
        : uiText("План поставлен в очередь на отправку заказчику."));
      load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    }
  };

  const exportPdf = async (item) => {
    try {
      const res = await auditProgramsApi.pdf(item.id);
      downloadBlob(res.data, `${item.number || `audit_program_${item.id}`}.pdf`);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  if (loading) return <LoadingState message={uiText("Загружаем плана аудита…")} />;

  return (
    <div className="audit-programs-page workspace-page">
      <PageIntro
        icon={ClipboardList}
        title={uiText("Планы аудитов")}
        description={uiText("Область и почасовой план проверки: процессы, подразделения, требования, участники объекта и команда аудиторов.")}
      />

      {error ? <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div> : null}
      {success ? <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div> : null}
      <DraftRecoveryNotice buffer={buffer} label={uiText("план аудита — проверьте выбранный аудит")} disabled={saving || editorLoading}/>
      <SaveStatus saving={saving} error={error} dirty={buffer.count>0} recovery={buffer.recovery} savedAt={success}/>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div className="card-title">{form.id ? uiText("Редактирование плана") : uiText("Новый план аудита")}</div>
          <button type="button" className="btn btn-outline btn-sm" disabled={saving || editorLoading} onClick={()=>startCreate()}>
            {form.id ? <X size={14} /> : <Plus size={14} />} {form.id ? uiText("Отменить редактирование") : uiText("Новая")}
          </button>
        </div>
        <div className="card-body">
          <form className="audit-program-form" onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <fieldset className="ap-form-lock" disabled={saving || editorLoading || !!buffer.recovery}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{uiText("Аудит")}</label>
                <select className="form-control" value={form.audit_id} onChange={(e) => handleAuditChange(e.target.value)} required>
                  <option value="">{uiText("Выберите аудит...")}</option>
                  {audits.map((audit) => <option key={audit.id} value={audit.id}>{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Номер")}</label>
                <input className="form-control" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Статус")}</label>
                <select className="form-control" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">{uiText("Черновик")}</option>
                  <option value="sent">{uiText("Направлен")}</option>
                  <option value="agreed">{uiText("Утверждён")}</option>
                  <option value="active">{uiText("В работе")}</option>
                  <option value="completed">{uiText("Завершён")}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Версия")}</label>
                <input className="form-control" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
              </div>
            </div>
            {currentAudit?.meta?.workflow_version === 'compliance-v2' && <p className="context-help">{currentAudit.customer ? uiText("Утверждённый план автоматически направляется представителю за 7 дней до аудита. При более позднем утверждении — при ближайшей проверке очереди (до минуты).") : uiText("В этом аудите нет представителя: внешняя отправка плана не выполняется.")}</p>}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{uiText("Объект аудита")}</label>
                <input className="form-control" value={form.audit_object} onChange={(e) => setForm({ ...form, audit_object: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Площадка / адрес")}</label>
                <input className="form-control" value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Представитель объекта")}</label>
                <input className="form-control" value={form.object_representative} onChange={(e) => setForm({ ...form, object_representative: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{uiText("Область аудита / требования")}</label>
              <textarea
                className="form-control"
                rows={4}
                value={form.scope_text}
                onChange={(e) => setForm({ ...form, scope_text: e.target.value })}
                placeholder={uiText("Например: Требования договора поставки\nISO 9001, п. 8.4\nПроцесс закупок / склад / входной контроль")}
              />
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ fontWeight: 700 }}>{uiText("Почасовой план аудита")}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={addScheduleRow}><Plus size={14} />{uiText(" Добавить строку")}</button>
                </div>
              </div>
              <div className="audit-program-schedule-wrap" style={{ overflowX: 'auto' }}>
                <table className="table audit-program-schedule-table">
                  <thead>
                    <tr>
                      <th>{uiText("Дата")}</th>
                      <th>{uiText("Время")}</th>
                      <th>{uiText("Процесс / блок")}</th>
                      <th>{uiText("Подразделение")}</th>
                      <th>{uiText("Требования")}</th>
                      <th>{uiText("Участники объекта")}</th>
                      <th>{uiText("Аудитор")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.schedule_items.map((row, idx) => (
                      <tr key={idx}>
                        <td><SafeDateInput aria-label={uiText('Дата строки плана')} value={shortDate(row.date)} onChange={value => updateSchedule(idx, 'date', value)} /></td>
                        <td>
                          <div className="audit-program-time-range">
                            <SafeTimeInput aria-label={uiText("Время начала")} value={row.start_time || ''} onChange={value => updateSchedule(idx, 'start_time', value)} />
                            <span aria-hidden="true">—</span>
                            <SafeTimeInput aria-label={uiText("Время окончания")} value={row.end_time || ''} onChange={value => updateSchedule(idx, 'end_time', value)} />
                          </div>
                        </td>
                        <td><input className="form-control" value={row.process || ''} onChange={(e) => updateSchedule(idx, 'process', e.target.value)} /></td>
                        <td><input className="form-control" value={row.department || ''} onChange={(e) => updateSchedule(idx, 'department', e.target.value)} /></td>
                        <td><textarea className="form-control" rows={2} value={row.requirements_block || ''} onChange={(e) => updateSchedule(idx, 'requirements_block', e.target.value)} /></td>
                        <td><textarea className="form-control" rows={2} value={row.auditee_participants || ''} onChange={(e) => updateSchedule(idx, 'auditee_participants', e.target.value)} /></td>
                        <td>
                          {currentTeam.length ? (
                            <select className="form-control" value={row.auditor_user_id || ''} onChange={(e) => setScheduleAuditor(idx, e.target.value)}>
                              <option value="">{uiText("Не выбран")}</option>
                              {currentTeam.map((member) => (
                                <option key={member.assignment_id || member.auditor_user_id} value={member.auditor_user_id}>
                                  {member.auditor_name || uiText("Аудитор #{v0}", {v0: (member.auditor_user_id)})}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input className="form-control" value={row.auditor_name || ''} onChange={(e) => updateSchedule(idx, 'auditor_name', e.target.value)} />
                          )}
                        </td>
                        <td>
                          <button type="button" className="ap-button ap-button--secondary ap-control-icon" onClick={() => removeScheduleRow(idx)} aria-label={uiText("Удалить строку")}>
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn btn-outline" onClick={resetForm}>{uiText("Очистить")}</button>
              <button type="submit" className="btn btn-primary" disabled={saving}><Save size={15} /> {saving ? uiText("Сохранение...") : uiText("Сохранить план")}</button>
            </div>
            </fieldset>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body table-wrap" style={{ overflowX: 'auto' }}>
          <table className="table responsive-table audit-program-registry-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{uiText("Номер")}</th>
                <th>{uiText("Объект")}</th>
                <th>{uiText("Площадка")}</th>
                <th>{uiText("Область / план")}</th>
                <th>{uiText("Статус")}</th>
                <th>{uiText("Действия")}</th>
              </tr>
            </thead>
            <tbody>
              {programs.length ? programs.map((p) => (
                <tr key={p.id}>
                  <td data-label="ID">{p.id}</td>
                  <td data-label={uiText("Номер")}>{p.number || uiText("План #{v0}", {v0: (p.id)})}</td>
                  <td data-label={uiText("Объект")}>{p.audited_company?.name || p.audit_object || '-'}</td>
                  <td data-label={uiText("Площадка")}>{p.site_address || '-'}</td>
                  <td data-label={uiText("Область / план")}>
                    <div className="audit-program-summary">
                      <strong title={programScopeSummary(p.scope_items)}>{programScopeSummary(p.scope_items)}</strong>
                      <span>{programScheduleSummary(p.schedule_items)}</span>
                    </div>
                  </td>
                  <td data-label={uiText("Статус")}><span className="badge badge-gray">{formatStatus(p.status, 'program')}</span></td>
                  <td data-label={uiText("Действия")}>
                    <div className="audit-program-actions row-primary-action">
                      <button className="btn btn-outline btn-sm" type="button" onClick={() => startEdit(p)}><Pencil size={14} />{uiText(" Открыть")}</button>
                      <ActionMenu
                        label={uiText("Другие действия с программой {v0}", {v0: (p.number || `#${p.id}`)})}
                        actions={[
                          { key: 'pdf', label: uiText("Скачать PDF"), icon: FileDown, onClick: () => exportPdf(p) },
                          { key: 'object', label: p.sent_at ? uiText("Повторно отправить объекту") : uiText("Направить объекту"), icon: Send, onClick: () => queue(p.id, 'object') },
                          { key: 'customer', label: p.sent_at ? uiText("Повторно отправить заказчику") : uiText("Направить заказчику"), icon: Send, onClick: () => queue(p.id, 'customer') },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              )) : <tr className="responsive-table-state-row"><td colSpan={7}>{uiText("Нет программ аудита.")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
